/* eslint-disable */
/**
 * Grouping utilities for Product Dashboard
 * 
 * Provides functions to group SRs by expected version and calculate sort order.
 */

import { ProductTimelineItem } from '../types/productDashboard';

export interface SRGroup {
  groupKey: string;              // "Shift-26.01", "Shift-26.02", "No Expected Version"
  groupLabel: string;            // Display name for the group
  items: ProductTimelineItem[];  // SRs in this group
  isExpanded: boolean;           // Expand/collapse state
  sortOrder: number;             // For sorting groups (earliest version first)
  isPast: boolean;               // Whether this version is in the past
}

/**
 * Calculate sort order for a group key
 * 
 * @param groupKey - The group key (e.g., "Shift-26.01" or "No Expected Version")
 * @returns Numeric sort order (lower = earlier)
 */
export function calculateSortOrder(groupKey: string): number {
  if (groupKey === 'No Expected Version') {
    return 9999; // Last
  }

  // Parse "Shift-YY.MM" to numeric value for sorting
  const match = groupKey.match(/Shift-(\d{2})\.(\d{2})/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    return year * 100 + month; // e.g., 2601 for "Shift-26.01"
  }

  return 0; // Fallback
}

/**
 * Get current year and month as numeric value
 * 
 * @returns Current date in YYMM format (e.g., 2602 for Feb 2026)
 */
export function getCurrentYearMonth(): number {
  const now = new Date();
  const year = now.getFullYear() % 100; // Last 2 digits of year
  const month = now.getMonth() + 1; // 1-12
  return year * 100 + month;
}

/**
 * Check if a version is in the past
 * 
 * @param groupKey - The group key (e.g., "Shift-26.01")
 * @returns True if the version is before the current month
 */
export function isVersionInPast(groupKey: string): boolean {
  if (groupKey === 'No Expected Version') {
    return false; // Never collapse "No Expected Version"
  }

  const sortOrder = calculateSortOrder(groupKey);
  const currentYearMonth = getCurrentYearMonth();
  
  return sortOrder > 0 && sortOrder < currentYearMonth;
}

/**
 * Group SRs by their expected version
 * 
 * @param items - Array of ProductTimelineItem to group
 * @returns Array of SRGroup objects, sorted by version (earliest first)
 */
export function groupSRsByExpectedVersion(items: ProductTimelineItem[]): SRGroup[] {
  // 1. Group by expected_version
  const groupMap = new Map<string, ProductTimelineItem[]>();

  items.forEach((item) => {
    const key = item.expected_version || 'No Expected Version';
    if (!groupMap.has(key)) {
      groupMap.set(key, []);
    }
    groupMap.get(key)!.push(item);
  });

  // 2. Convert to SRGroup array
  const groups: SRGroup[] = Array.from(groupMap.entries()).map(([key, groupItems]) => {
    const isPast = isVersionInPast(key);
    return {
      groupKey: key,
      groupLabel: key === 'No Expected Version' ? 'No Expected Version' : key,
      items: groupItems,
      isExpanded: !isPast, // Collapse past versions by default
      sortOrder: calculateSortOrder(key),
      isPast,
    };
  });

  // 3. Sort groups by sortOrder
  groups.sort((a, b) => a.sortOrder - b.sortOrder);

  return groups;
}
